import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {SubjektasApi} from "../../api/subjektas.api";
import {GiiRegistracijaApi} from "../../api/gii-registracija.api";
import {GiiAtsakomybesBudasApi} from "../../api/gii-atsakomybes-budas.api";
import {DatePipe} from "@angular/common";
import {KlasifikatoriusApi} from "../../api/klasifikatorius.api";
import {TranslateService} from "@ngx-translate/core";
import {RegPerziuraService} from "../../service/reg-perziura.service";
import {SharedDataService} from "../../service/shared-data.service";
import {PdfExportService} from "../../service/pdf-export.service";
import {PDFExportComponent} from "@progress/kendo-angular-pdf-export";
import {LanguageService} from "../../service/language.service";
import {distinctUntilChanged, Subject, takeUntil} from "rxjs";
import {RegKoregavimasEnum, RegKoregavimasEnumType} from "../../enums/reg-koregavimas-enum";

@Component({
  selector: 'app-reg-perziura',
  templateUrl: './reg-perziura.component.html',
  styleUrls: ['./reg-perziura.component.css']
})
export class RegPerziuraComponent implements OnInit {
  @Input() regPerziura : any;
  @ViewChild('pdfExport') pdfExport!: PDFExportComponent;

  selectedRow: any = null;
  isRowSelected = false;
  giiRegistracijaApi: GiiRegistracijaApi = new GiiRegistracijaApi();
  subjektasApi: SubjektasApi = new SubjektasApi();
  klasifikatoriusList: KlasifikatoriusApi[] = [];
  busenuIstorija: any[] = [];
  loadPdf = false;
  isPrinting = false;
  private destroy$ = new Subject<void>();
  RegKoregavimasEnum = RegKoregavimasEnum;

  constructor(public perziuraService: RegPerziuraService,
              private datePipe: DatePipe,
              private translate: TranslateService,
              public sharedDataService: SharedDataService,
              private pdfService: PdfExportService,
              private languageService: LanguageService) {
  }

  ngOnInit(): void {
    this.perziuraService.perziura$
      .pipe(
        distinctUntilChanged((a, b) => a?.selectedId === b?.selectedId),
        takeUntil(this.destroy$))
      .subscribe(perziura => {
        if (perziura?.selectedId) {
          this.isRowSelected = false;
          this.loadRegistracija(perziura.selectedId);
        }
      });

    this.sharedDataService.getklasifikatoriusApiSubject()
      .pipe(takeUntil(this.destroy$))
      .subscribe(klasifikatoriusList => {
        this.klasifikatoriusList = klasifikatoriusList;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(void 0);
    this.destroy$.complete();
  }

  private loadRegistracija(selectedId: number): void {
    this.sharedDataService.setIsLoading(true);
    this.perziuraService.fetchGiiRegistracijaById(selectedId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.giiRegistracijaApi = data;
        this.subjektasApi = data.subjektasApi;
        this.busenuIstorija = data.giiRegKoregavimasApiList;
        this.sharedDataService.setIsLoading(false);
      });
  }

  public async printGrid(): Promise<void> {
    await this.pdfService.print(this, () => this.pdfExport);
  }

  generateVeiklosBudai(id: number | null | undefined, atsakBudaiApi: GiiAtsakomybesBudasApi[] | null): string {
    const selectedLanguage = this.translate.currentLang;

    const formatDescription = (clPavadinimas: string, formattedDate: string | null) => {
      return selectedLanguage === 'lt'
        ? ` (${clPavadinimas} nuo ${formattedDate})`
        : ` (${clPavadinimas} from ${formattedDate})`;
    };

    const getDescription = (clatsId: number | null | undefined, clCode: string) => {
      const klasReiksme = this.getClByCodeAndId(this.klasifikatoriusList, clCode, clatsId);
      return this.languageService.translatePavadinimas(klasReiksme.pavadinimas);
    };

    if (id === this.getClPvId() && atsakBudaiApi !== null) {
      const atsakBudaiDescriptions = atsakBudaiApi.map(item => {
        const clPavadinimas = getDescription(item.clatsId, 'CL124');
        const formattedDate = this.datePipe.transform(item.galiojaNuo, 'yyyy-MM-dd');
        return formatDescription(clPavadinimas, formattedDate);
      }).join(', ');

      return getDescription(id, 'CL118') + atsakBudaiDescriptions;
    } else {
      return getDescription(id, 'CL118');
    }
  }

  getLatestKoregData(): Date {
    return this.giiRegistracijaApi.giiRegKoregavimasApiList
      .filter(item => item.koregData)
      .map(item => new Date(item.koregData as Date))
      .reduce((latest, current) => (current > latest ? current : latest), new Date(0));
  }

  translateClassifierLabel(id: string): string {
    for (const klasifikatorius of this.klasifikatoriusList) {
      const klasifikatoriausReiksme = klasifikatorius.klasifikatoriausReiksmeApiList?.find(y => y.id === Number(id));

      if (klasifikatoriausReiksme) {
        return this.languageService.translatePavadinimas(klasifikatoriausReiksme.pavadinimas);
      }
    }

    return '';
  }

  getSrautoClWithDates(id: number, clTipas: string): string {
    const klasifikatorius = this.klasifikatoriusList.find(x => x.kodas === clTipas);

    if (klasifikatorius) {
      const klasifikatoriausReiksme = klasifikatorius.klasifikatoriausReiksmeApiList?.find(y => y.id === id);

      if (klasifikatoriausReiksme) {
        const lang = this.translate.currentLang;
        const pavadinimas = this.languageService.translatePavadinimas(klasifikatoriausReiksme.pavadinimas);
        const prefix = lang === 'lt' ? 'nuo' : 'from';
        const date = this.datePipe.transform(klasifikatoriausReiksme.dateFrom, 'yyyy-MM-dd');
        return `${pavadinimas} (${prefix} ${date})`;
      }
    }

    return '';
  }

  getClPvId(): number | null {
    const clVeiklosBudai = this.klasifikatoriusList.find((x) => x.kodas === 'CL118');

    if (clVeiklosBudai) {
      const clPvIdObject = clVeiklosBudai.klasifikatoriausReiksmeApiList.find((x) => x.kodas === 'PV');

      if (clPvIdObject) {
        return clPvIdObject.id;
      }
    }

    return null;
  }

  getClByCodeAndId(data: KlasifikatoriusApi[], clKodas: string, clId: number | null | undefined): any {
    let pavadinimas;
    const klasifikatorius = data.find(item => item.kodas === clKodas);
    if (klasifikatorius && clId !== null) {
      pavadinimas = klasifikatorius.klasifikatoriausReiksmeApiList.find(x=> x.id === clId);
    }
    return pavadinimas;
  }

  onRowSelect(selectedItem: any): void {
    this.selectedRow = selectedItem;
    this.isRowSelected = true;
  }

  formatValue(dataItem: any, field: string): string {
    const val = dataItem[field];
    if (val == null) return '-';

    if (dataItem.laukoPav.includes('clId')) {
      return this.translateClassifierLabel(val);
    } else if (dataItem.laukoPav.includes('busena')) {
      return this.translate.instant('label.busena.' + val);
    } else {
      return val;
    }
  }

  isEnumValue(value: string): boolean {
    return RegKoregavimasEnum.includes(value as RegKoregavimasEnumType);
  }
}
